import { useCallback } from 'react';

import type { AiCoach_RespondToUserMessageInput as AiCoachMessageInput } from '__generated__/graphql-types';

import { useLocation } from 'js/lib/useRouter';

import { useTracker } from '@coursera/event-pulse/react';

import useCoachTriggerMessage from 'bundles/ai-coach-platform/components/patterns/chat/hooks/useCoachTriggerMessage';
import type { AiCoachPlatform_RespondToUserMessageMutation as AiCoachPlatformRespondToUserMessageMutation } from 'bundles/ai-coach-platform/queries/__generated__/aiCoachRespondToUserMessageMutation';
import { useCoachState } from 'bundles/unified-common/providers/CoachStateProvider';

type CoachConfig = Pick<AiCoachMessageInput, 'config' | 'courseraContext' | 'additionalContext'>;

type Props = {
  /** Coach config for the prompt, used for initial query when the prompt pill gets clicked */
  promptCoachConfig: CoachConfig;
  /**
   * Optional: Coach config for the router prompt, used for subsequent queries in the chat.
   * If not provided, the promptCoachConfig will be used for subsequent queries.
   */
  promptCoachRouterConfig?: CoachConfig;
  /** Optional: Whether to start a new session on each click, clearing previous messages. Defaults to false. */
  newSessionOnClick?: boolean;
};

/**
 * A hook that handles the click event of an AI prompt pill, a wrapper of useCoachTriggerMessage hook with additional functionalities:
 * 1. Track the click event
 * 2. Set the Coach config to router prompt for subsequent queries in the chat if provided
 * 3. Manage Coach chat window state (open/close, fullscreen/minimized)
 */
const useAiPromptPillClick = ({ promptCoachConfig, promptCoachRouterConfig, newSessionOnClick = false }: Props) => {
  const track = useTracker();
  const location = useLocation();
  const { coachOpen, setCoachOpen, isMinimizedChat, toggleFullScreen, setContextConfig, setTriggerElementRef } =
    useCoachState();

  const handleAiPromptPillClick = () => {
    track('interact_coach', {
      coachInteractionType: 'click_action',
      coach: {
        mode: promptCoachConfig.config.coachModeId,
      },
      coachAction: promptCoachConfig.config.coachActionId,
    });

    // set Coach config to router prompt for later learner queries in the chat
    setContextConfig(promptCoachRouterConfig ?? promptCoachConfig);
    // set the trigger element ref to the active element
    setTriggerElementRef(document.activeElement as HTMLElement | null);

    if (!coachOpen) {
      // open Coach chat if it's not already open
      setCoachOpen(true);
    } else if (isMinimizedChat) {
      // toggle fullscreen if Coach chat is minimized
      toggleFullScreen();
    }
  };

  const onCoachActionClick = useCoachTriggerMessage({
    config: promptCoachConfig.config,
    newSessionOnClick,
    onClick: handleAiPromptPillClick,
    skipSessionUpdate: false,
    enableContinuousChat: false,
    enableTextStreaming: true,
    enableLoggedOutMode: true,
    location,
    additionalContext: promptCoachConfig.additionalContext,
    courseraContext: promptCoachConfig.courseraContext,
  });

  return useCallback<
    ({
      message,
      label,
    }: {
      message?: string;
      label: string;
    }) => Promise<void | AiCoachPlatformRespondToUserMessageMutation | null>
  >(
    ({ message, label }) => {
      return onCoachActionClick({ message, label });
    },
    [onCoachActionClick]
  );
};

export default useAiPromptPillClick;
